// src/pages/markets/board/card/body/TrendBody.tsx

import { Box, Skeleton, Typography } from '@mui/material';
import type { MarketSeries, ScreenerMarket } from '../../api/boardTypes';
import { CardTrendChart } from '../CardTrendChart';
import { truncate } from '../cardFormat';

export interface TrendBodyProps {
    market: ScreenerMarket | null;
    series: MarketSeries | null;
    impliedProb: number | null;
    groupColor: string;
    window: string;
    loading: boolean;
    error: boolean;
}

function Centered({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                border: '1px dashed',
                borderColor: 'divider',
                px: 1,
            }}
        >
            <Typography sx={{ fontSize: '11px', color: 'text.disabled', textAlign: 'center' }}>
                {children}
            </Typography>
        </Box>
    );
}

export function TrendBody({
    market,
    series,
    impliedProb,
    groupColor,
    window,
    loading,
    error,
}: TrendBodyProps) {
    if (!market) return <Centered>No market selected</Centered>;
    if (error) return <Centered>Price history unavailable</Centered>;

    if (loading && !series) {
        return <Skeleton variant="rounded" sx={{ height: '100%' }} />;
    }

    if (!series) return <Centered>No price history yet</Centered>;

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
                sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    mb: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {truncate(market.outcome_yes_label || market.question || '', 34)}
            </Typography>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <CardTrendChart
                    points={series.points}
                    referenceValue={impliedProb}
                    referenceColor={groupColor}
                    windowLabel={window}
                />
            </Box>
        </Box>
    );
}